import { useQuery } from '@tanstack/react-query';
import ms from 'ms';
import { useMemo } from 'react';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import { getPublishedNewsData } from 'hb-react/shared/apps/News/NewsPublished/utils/getPublishedNewsData';
import { fetchNewsPublishedPath } from 'hb-react/shared/apps/News/NewsPublished/utils/newsUrls';
import { getQueryKey } from 'hb-react/shared/apps/News/NewsPublished/utils/queryUtils';
import type {
  GetNewsResponse,
  PublishedNewsData,
} from 'hb-react/shared/apps/News/types';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosWithSyntaxHandlers as axios } from 'hb-react/shared/utils/axios';

interface UseNewsPublishedReturn {
  error: Error | null;
  isLoading: boolean;
  news: PublishedNewsData | undefined;
}

export const useNewsPublished = (
  newsId?: string | number,
  userId?: number | null,
): UseNewsPublishedReturn => {
  const { topicId, whitelabelURL } = useTopicContext();
  const { flagEvNewsRevamp } = useFeatureFlag();
  const { data, isLoading, error } = useQuery<GetNewsResponse>({
    queryKey: getQueryKey(newsId),
    enabled: !!newsId,
    queryFn: async () => {
      try {
        return await fetchNewsPublished({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          newsId: newsId!,
          topicId,
          userId,
          whiteLabelEnabled: !!whitelabelURL,
          flagEvNewsRevamp,
        });
      } catch (error) {
        if (error?.response?.status === 403) {
          displayXhrError(error);
        }
        throw error;
      }
    },
    staleTime: ms('5m'),
  });

  const news = useMemo(() => data && getPublishedNewsData(data), [data]);

  return {
    error,
    isLoading,
    news,
  };
};

interface FetchNewsPublishedParams {
  newsId: string | number;
  topicId?: number | null;
  userId?: number | null;
  whiteLabelEnabled: boolean;
  flagEvNewsRevamp: boolean;
}

const fetchNewsPublished = ({
  newsId,
  topicId,
  userId,
  whiteLabelEnabled,
  flagEvNewsRevamp,
}: FetchNewsPublishedParams): Promise<GetNewsResponse> =>
  axios
    .get(
      fetchNewsPublishedPath({
        newsId,
        topicId,
        userId,
        whiteLabelEnabled,
        flagEvNewsRevamp,
      }),
    )
    .then((res) => res.data);
